<template>
    <div class="auth-page loading">
        <spinner-loader :loadingStuff="loadingStuff" />
        <main>
            <div class="content content--side">
                <header class="codrops-header uk-flex uk-flex-center">
                    <TenantLogo logo-class="login-logo" />
                    <h1 class="uk-margin-remove uk-text-center login-header">
                        {{ $store.getters.clubNameShort }}
                        <span>Reset Password</span>
                    </h1>
                </header>

                <!-- Invalid/Expired Link -->
                <div v-if="error" class="form">
                    <div class="error-message">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <h2>{{ errorTitle }}</h2>
                        <p>{{ errorMessage }}</p>
                        <router-link to="/login" class="green-button">Back to Login</router-link>
                    </div>
                </div>

                <!-- Success Message -->
                <div v-else-if="success" class="form">
                    <div class="success-message">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <h2>Password Reset Successfully!</h2>
                        <p>Your password has been updated. You can now log in with your new password.</p>
                        <router-link to="/login" class="green-button">Go to Login</router-link>
                    </div>
                </div>

                <!-- Reset Password Form -->
                <form v-else class="form" @submit.prevent="resetPassword">
                    <p class="form-description">Enter your new password below.</p>

                    <div class="form__item">
                        <label class="form__label" for="new-password">New Password</label>
                        <input class="form__input" type="password" v-model="newPassword" id="new-password"
                            placeholder="Enter new password" minlength="6" required />
                    </div>

                    <div class="form__item">
                        <label class="form__label" for="confirm-password">Confirm Password</label>
                        <input class="form__input" type="password" v-model="confirmPassword" id="confirm-password"
                            placeholder="Confirm new password" minlength="6" required />
                    </div>

                    <div v-if="passwordError" class="password-error">
                        {{ passwordError }}
                    </div>

                    <div class="password-requirements">
                        <p>Password must:</p>
                        <ul>
                            <li :class="{ valid: newPassword.length >= 6 }">Be at least 6 characters long</li>
                            <li :class="{ valid: newPassword === confirmPassword && confirmPassword.length > 0 }">Match
                                confirmation</li>
                        </ul>
                    </div>

                    <div class="form__item form__item--actions">
                        <button type="submit" :disabled="!isFormValid || loadingStuff">
                            {{ loadingStuff ? 'Resetting...' : 'Reset Password' }}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    </div>
</template>

<script>
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import SpinnerLoader from "@/components/LoadingSpinning";
import TenantLogo from "@/components/TenantLogo/index.vue";
import { Users } from "@/services/user-service.js";

export default {
    name: "ResetPassword",
    components: {
        SpinnerLoader,
        TenantLogo,
    },
    data() {
        return {
            loadingStuff: true,
            newPassword: "",
            confirmPassword: "",
            passwordError: "",
            error: false,
            errorTitle: "",
            errorMessage: "",
            success: false,
            oobCode: null,
            email: null,
        };
    },
    computed: {
        isFormValid() {
            return (
                this.newPassword.length >= 6 &&
                this.newPassword === this.confirmPassword
            );
        },
    },
    async mounted() {
        // Get the action code from URL
        const urlParams = new URLSearchParams(window.location.search);
        this.oobCode = urlParams.get("oobCode");
        const mode = urlParams.get("mode");

        // If no oobCode, user likely came here after Firebase's default reset page
        // (via continueUrl redirect) - show success message
        if (!this.oobCode) {
            this.success = true;
            this.loadingStuff = false;
            return;
        }

        // If mode is not resetPassword but we have oobCode, still try to use it
        if (mode && mode !== "resetPassword") {
            this.showError("Invalid Link", "This link is not for password reset.");
            return;
        }

        // Verify the code is valid
        try {
            const auth = getAuth();
            this.email = await verifyPasswordResetCode(auth, this.oobCode);
            this.loadingStuff = false;
        } catch (error) {
            console.error("Code verification error:", error);

            if (error.code === "auth/expired-action-code") {
                this.showError("Link Expired", "This password reset link has expired. Please request a new one.");
            } else if (error.code === "auth/invalid-action-code") {
                this.showError("Invalid Link", "This password reset link is invalid or has already been used.");
            } else {
                this.showError("Error", "Something went wrong. Please try again.");
            }
        }
    },
    methods: {
        showError(title, message) {
            this.error = true;
            this.errorTitle = title;
            this.errorMessage = message;
            this.loadingStuff = false;
        },
        async resetPassword() {
            this.passwordError = "";

            if (this.newPassword !== this.confirmPassword) {
                this.passwordError = "Passwords do not match";
                return;
            }

            if (this.newPassword.length < 6) {
                this.passwordError = "Password must be at least 6 characters";
                return;
            }

            this.loadingStuff = true;

            try {
                const auth = getAuth();

                // Confirm the password reset with Firebase
                await confirmPasswordReset(auth, this.oobCode, this.newPassword);

                // Also sync to MySQL database
                await this.syncPasswordToMySQL();

                this.success = true;
                this.$snack.success({ text: "Password reset successfully!" });

            } catch (error) {
                console.error("Password reset error:", error);

                if (error.code === "auth/expired-action-code") {
                    this.showError("Link Expired", "This password reset link has expired. Please request a new one.");
                } else if (error.code === "auth/weak-password") {
                    this.passwordError = "Password is too weak. Please choose a stronger password.";
                    this.loadingStuff = false;
                } else {
                    this.passwordError = "Failed to reset password. Please try again.";
                    this.loadingStuff = false;
                }
            }
        },
        async syncPasswordToMySQL() {
            // Sync the new password to MySQL for users who have the email
            try {
                const data = {
                    email: this.email,
                    password: this.newPassword,
                    action: "syncPasswordByEmail",
                };
                await Users.find(data);
            } catch (error) {
                // Don't block the reset if MySQL sync fails
                console.error("MySQL sync error:", error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../styles/auth-styles.scss";
</style>

<style lang="scss" scoped>
.form-description {
    text-align: center;
    margin-bottom: 1.5rem;
    color: #666;
}

.password-error {
    color: #dc3545;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #f8d7da;
    border-radius: 0.25rem;
}

.password-requirements {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    font-size: 0.875rem;

    p {
        margin-bottom: 0.5rem;
        font-weight: 500;
    }

    ul {
        margin: 0;
        padding-left: 1.25rem;
    }

    li {
        color: #6c757d;
        margin-bottom: 0.25rem;

        &.valid {
            color: #28a745;

            &::marker {
                content: "✓ ";
            }
        }
    }
}

.error-message,
.success-message {
    text-align: center;
    padding: 2rem;

    svg {
        margin-bottom: 1rem;
    }

    h2 {
        margin-bottom: 0.5rem;
        font-size: 1.5rem;
    }

    p {
        color: #666;
        margin-bottom: 1.5rem;
    }
}

.error-message svg {
    color: #dc3545;
}

.success-message svg {
    color: #28a745;
}

.green-button {
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: #28a745;
    border: 1px solid #28a745;
    color: white;
    padding: 0.5rem 1.5rem;
    font-size: 1rem;
    border-radius: 0.25rem;
    transition: all 0.15s ease-in-out;

    &:hover {
        background-color: #218838;
        border-color: #1e7e34;
        color: white;
    }
}

button[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
